/**
 * O TRATO DO DIA — a tela mais usada do sistema.
 *
 * Ela junta os três patrões e mostra qual deles mandou:
 *
 *   O PEIXE   — a tabela de arraçoamento, em % do peso vivo
 *   A ÁGUA    — o teto em kg/ha/dia daquele tanque naquela estação
 *   O DIA     — o coeficiente da leitura da manhã
 *
 *     base = o MENOR entre peixe e água
 *     sugerido = base × coeficiente do dia
 *
 * A FRASE «QUEM LIMITOU» É OBRIGATÓRIA. Sem ela o número é ordem; com ela é
 * explicação, e o tratador pode discordar sabendo de quê — que é o que a
 * regra 1 pede. Nesta fazenda a resposta vai ser «a água» na maior parte do
 * ciclo, e saber disso muda decisão de povoamento, não de trato.
 *
 * A CONTA RODA AQUI, no aparelho, com parâmetros que o servidor mandou. Se
 * morasse no servidor, o tratador sem sinal ficaria sem sugestão exatamente na
 * beira do tanque, que é onde ele está.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  anularTrato, listarMotivos, listarRonda, numeroDoParametro, parametrosDoCalculo,
  registrarFalta, registrarTrato, verGalpao,
  type MotivoDeFalta, type ParametrosDoCalculo, type PontoDaRonda, type RacaoNoGalpao,
} from '../acesso/api'
import {
  anulacoesPendentes, descartar, faltasPendentesDe, pendentes, tratosPendentesDe,
} from '../acesso/fila'
import { movimentosDaFila, oGalpaoTem, type FaixaDeRacao } from '../dominio/estoque'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'
import { comPendentes } from './Ronda'
import { Agrupamento, VoltarAoTopo } from './Agrupamento'
import { EscolherRacao, nomeDaRacao, type Especificacao } from './EscolherRacao'
import {
  aguaDaConta, pctDoPesoVivo, tendenciaDoOxigenio, tendenciaEmPalavras, tetoDoTanque,
  vereditoDaAgua, type MedidaUsada,
} from '../dominio/agua'
import { pesoApos, type Curva } from '../dominio/crescimento'
import { emSacos, g, inteiro, m2, m2ParaHa, numero, numeroPtBr } from '../dominio/unidades'

export function Trato() {
  const [dia, setDia] = useState(hojeISO())
  const [versao, setVersao] = useState(0)
  const [pontos, setPontos] = useState<PontoDaRonda[]>([])
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [motivos, setMotivos] = useState<MotivoDeFalta[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [faltaGeral, setFaltaGeral] = useState(false)
  // O GALPÃO, lido uma vez por abertura da tela, e só para o aviso da ração.
  // Falhou (sem sinal)? Fica nulo, e o aviso cala: nada nesta tela depende dele.
  const [galpao, setGalpao] = useState<RacaoNoGalpao[] | null>(null)

  useEffect(() => {
    let vivo = true
    verGalpao()
      .then((g) => {
        if (vivo) setGalpao(g.racoes)
      })
      .catch(() => {
        /* sem galpão, sem aviso — o trato segue igual */
      })
    return () => {
      vivo = false
    }
  }, [])

  const recarregar = useCallback(async () => {
    try {
      const [r, p, m] = await Promise.all([listarRonda(dia), parametrosDoCalculo(), listarMotivos()])
      setPontos(r)
      setCalculo(p)
      setMotivos(m)
      setErro(null)
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
    } finally {
      setCarregando(false)
    }
  }, [dia])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  const curva: Curva | null = useMemo(
    () =>
      calculo
        ? {
            a: numeroDoParametro(calculo.parametros, 'crescimento.curva.a', 0.135),
            b: numeroDoParametro(calculo.parametros, 'crescimento.curva.b', 0.00613),
            fator: numeroDoParametro(calculo.parametros, 'crescimento.fator_calibracao', 1),
          }
        : null,
    [calculo],
  )

  if (carregando) return <p className="aviso">Carregando…</p>
  if (!calculo || !curva) return <p className="aviso-erro">Faltam os parâmetros da conta.</p>

  void versao
  const pesoSaco = numeroDoParametro(calculo.parametros, 'estoque.peso_saco_kg', 25)
  // O que está na fila do aparelho conta como registrado: o servidor não sabe
  // dele ainda, mas o tanque já recebeu a ração.
  // O QUE ESTÁ NA FILA CONTA, inclusive o que ela DESFAZ. Anulação esperando
  // para subir já tirou o trato do dia para quem está olhando a tela — não
  // descontar aqui faria a recomendação continuar achando que o tanque recebeu
  // ração que o gestor acabou de cancelar.
  const anuladas = anulacoesPendentes()

  const comFila = pontos.map((p) => {
    const naFila = tratosPendentesDe(p.ciclo_id, dia)
    const anuladosAqui = p.tratos_do_dia.filter((t) => anuladas.has(t.id))
    const kgAnulado = anuladosAqui.reduce((soma, t) => soma + t.quantidade_kg, 0)
    const kgNaFila = naFila.reduce((soma, f) => {
      const unidade = f.corpo['unidade_informada']
      const qtd = Number(f.corpo['quantidade_informada'] ?? 0)
      return soma + (unidade === 'saco' ? qtd * pesoSaco : qtd)
    }, 0)
    return {
      ...p,
      // A LEITURA DE ÁGUA QUE AINDA ESTÁ NA FILA ENTRA NA CONTA — a mesma
      // junção da Ronda. Antes, quem gravava 2,6 mg/L na Ronda e abria o
      // Trato sem sincronizar lia «sem leitura hoje» e via o teto cheio.
      leituras: comPendentes(p, dia).leituras,
      tratado_hoje_kg: p.tratado_hoje_kg + kgNaFila - kgAnulado,
      tratos_hoje: p.tratos_hoje + naFila.length - anuladosAqui.length,
      faltas_hoje: p.faltas_hoje + faltasPendentesDe(p.ciclo_id, dia),
    }
  })
  // O «NINGUÉM TRATOU» É INTELIGENTE (05/10/2026, pedido do gestor): só entra
  // tanque SEM TRATO e SEM FALTA no dia. Antes entravam todos sem falta, e o
  // tanque que já tinha recebido ração no dia ganhava uma falta por cima —
  // fato que não aconteceu. O trato ainda na fila do celular conta como trato.
  const semTrato = comFila.filter(
    (p) => p.faltas_hoje === 0 && p.tratos_hoje <= 0 && p.tratado_hoje_kg <= 0,
  )
  const jaTratados = comFila.filter((p) => p.faltas_hoje === 0 && !semTrato.includes(p)).length

  return (
    <div className="trato">
      {erro && <p className="aviso-erro">{erro}</p>}

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      {comFila.length === 0 ? (
        <p className="aviso">Nenhum tanque com peixe estocado.</p>
      ) : (
        <>
          {/* A LEGENDA DA LINHA FECHADA, dita uma vez. Sem ela «75 | 50» é
              charada; com ela, é a frase mais curta possível para «dei 75 dos
              50 previstos». */}
          <p className="legenda-gaveta secundario">dado | previsto, em kg</p>

          <Agrupamento
            tela="trato"
            itens={comFila.map((ponto) => ({
              chave: ponto.ciclo_id,
              titulo: ponto.tanque,
              setor: ponto.setor,
              // PENDENTE é o tanque que ainda não recebeu nada no dia e não
              // tem falta marcada. É esse número que aparece na linha fechada
              // do setor e dispensa qualquer «setor preferido».
              pendente: ponto.tratado_hoje_kg <= 0 && ponto.faltas_hoje === 0,
              ponto,
            }))}
            resumo={(i) => (
              <ResumoDoTrato
                ponto={i.ponto}
                calculo={calculo}
                curva={curva}
                pesoSaco={pesoSaco}
                dia={dia}
              />
            )}
            ficha={(i) => (
              <ParadaDeTrato
                ponto={i.ponto}
                calculo={calculo}
                curva={curva}
                pesoSaco={pesoSaco}
                dia={dia}
                motivos={motivos}
                galpao={galpao}
                aoGravar={() => setVersao((v) => v + 1)}
              />
            )}
          />
          <VoltarAoTopo />

          <section className="bloco">
            {faltaGeral ? (
              <FormularioDeFalta
                titulo={jaTratados > 0 ? 'Falta nos tanques que não receberam trato' : 'Ninguém tratou neste dia'}
                ciclos={semTrato.map((p) => p.ciclo_id)}
                rotulos={Object.fromEntries(comFila.map((p) => [p.ciclo_id, p.tanque]))}
                motivos={motivos}
                dia={dia}
                aoFechar={() => setFaltaGeral(false)}
                aoGravar={() => {
                  setFaltaGeral(false)
                  setVersao((v) => v + 1)
                }}
              />
            ) : semTrato.length > 0 ? (
              /* ATENÇÃO, E NÃO ERRO. Ocre é a cor de «olhe isto» no resto do
                 app — «sem leitura», «1 pendente» —, e vinho é a de erro.
                 Marcar falta não é engano de ninguém: é um fato do dia sendo
                 registrado.

                 O destaque é do painel, e não do botão. Botão sólido e grande
                 seria a ação principal da tela, e aqui a ação principal é
                 tratar; este é o caminho de quando não se tratou. E ele fecha
                 o dia de oito tanques de uma vez — convidar o dedo seria a
                 escolha errada. O formulário de motivo que vem em seguida é o
                 que impede o toque distraído de virar registro. */
              <div className="falta-geral">
                <button type="button" className="discreto" onClick={() => setFaltaGeral(true)}>
                  {jaTratados > 0 ? 'Não tratei os outros' : 'Ninguém tratou'} — marcar falta em{' '}
                  {semTrato.length} {semTrato.length === 1 ? 'tanque' : 'tanques'}
                </button>
                {jaTratados > 0 && (
                  <p className="secundario">
                    {jaTratados === 1
                      ? 'O tanque que já recebeu trato hoje fica de fora.'
                      : `Os ${jaTratados} tanques que já receberam trato hoje ficam de fora.`}
                  </p>
                )}
              </div>
            ) : (
              // ZERO TANQUES NÃO É UM BOTÃO. «marcar falta em 0 tanques»,
              // desabilitado, é uma frase sem sentido ocupando o rodapé da
              // tela. Quando não sobra tanque, o que falta dizer é isso.
              <p className="secundario">
                {jaTratados > 0
                  ? 'Todos os tanques deste dia já têm trato ou falta registrada.'
                  : 'Todos os tanques deste dia já têm falta registrada.'}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  )
}

function ParadaDeTrato({
  ponto, calculo, curva, pesoSaco, dia, motivos, galpao, aoGravar,
}: {
  ponto: PontoDaRonda
  calculo: ParametrosDoCalculo
  curva: Curva
  pesoSaco: number
  dia: string
  motivos: MotivoDeFalta[]
  galpao: RacaoNoGalpao[] | null
  aoGravar: () => void
}) {
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const gravando = useRef(false)
  const [marcandoFalta, setMarcandoFalta] = useState(false)
  const [trocandoRacao, setTrocandoRacao] = useState(false)
  const [racaoEscolhida, setRacaoEscolhida] = useState<Especificacao | null>(
    () => racaoLembrada(ponto.tanque),
  )

  const conta = calcular(ponto, calculo, curva, pesoSaco, dia)
  // A TABELA SUGERE, A PESSOA DECIDE. A escolha guardada vale sobre a
  // sugestão: quem trocou uma vez trocou porque sabe o que tem no galpão, e
  // não deve ter de trocar de novo amanhã.
  const racao: Especificacao | null = racaoEscolhida ?? conta.racao

  if (ponto.faltas_hoje > 0) {
    return (
      <section className="parada lida">
        <div className="parada-topo">
          <strong>{ponto.tanque}</strong>
          <span className="etiqueta etiqueta-fria">falta registrada</span>
        </div>
      </section>
    )
  }

  function oferecer(unidade: 'saco' | 'kg', quantidade: number) {
    if (!racao) {
      setErro('não sei qual ração usar aqui — falta a especificação')
      return
    }
    // TRAVA DE TOQUE DUPLO, e ela precisa ser uma referência e não estado:
    // `registrarTrato` é síncrona, então `salvando` sobe e desce no mesmo
    // instante e o botão nunca chega a aparecer desabilitado. Dois toques
    // seguidos enfileiravam dois tratos, e nada na tela denunciava.
    if (gravando.current) return
    gravando.current = true
    setSalvando(true)
    setErro(null)
    try {
      registrarTrato({
        ciclo_id: ponto.ciclo_id,
        data: dia,
        hora: agora(),
        unidade_informada: unidade,
        quantidade_informada: quantidade,
        granulometria_min_mm: racao.min,
        granulometria_max_mm: racao.max,
        proteina_pct: racao.proteina,
        sugerido_kg: conta.sugerido,
        coeficiente_aplicado: conta.coeficiente,
        limitou: conta.limitou,
      }, { rotulo: ponto.tanque, pesoSaco })
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    } finally {
      setSalvando(false)
      // Solta a trava um instante depois: o que se quer barrar é o toque
      // repetido do mesmo gesto, não o próximo trato daqui a meia hora.
      setTimeout(() => {
        gravando.current = false
      }, 1200)
    }
  }

  return (
    <section className="parada">
      <div className="parada-topo">
        <strong>{ponto.tanque}</strong>
        <span className="etiqueta">{ponto.ciclo}</span>
        {ponto.tipo === 'represa' && <span className="etiqueta">represa fonte</span>}
      </div>

      <div className="secundario">
        {inteiro(ponto.peixes_hoje)} peixes · <em>estimados</em> em{' '}
        {conta.pesoHoje !== null ? `${inteiro(conta.pesoHoje)} g` : '—'} ·{' '}
        {conta.biomassa !== null ? `${numero(conta.biomassa / 1000, 1)} t` : '—'} ·{' '}
        {tendenciaEmPalavras(conta.tendencia)}
      </div>

      {/* O QUE JÁ ENTROU VEM ANTES DA RECOMENDAÇÃO, E VALE SEMPRE.
          Estava dentro do ramo «consigo sugerir», então nos dois casos em que
          o app não sugere — falta teto de água, ou água manda não tratar — a
          lista sumia. São justamente os dias em que saber o que já foi dado
          decide o que fazer: quem chega num tanque que já recebeu 3 sacos e lê
          «não tratar hoje» precisa dos dois fatos, não de um. */}
      <JaRegistrado ponto={ponto} dia={dia} pesoSaco={pesoSaco} aoMudar={aoGravar} />

      {conta.sugerido === null ? (
        <p className="resposta ruim">
          Falta informação para sugerir: {conta.porque}
        </p>
      ) : conta.coeficiente === 0 ? (
        <p className="resposta ruim">
          <strong>Não tratar hoje.</strong> {conta.frase}
          <br />
          <span className="secundario">{aguaDoDia(ponto, dia)}</span>
        </p>
      ) : (
        <>
          <p className="sugestao">
            <strong>
              {conta.sacosSugeridos > 0
                ? `${emSacos(conta.sacosSugeridos)} ${conta.sacosSugeridos === 1 ? 'saco' : 'sacos'}`
                : 'nada a oferecer agora'}
            </strong>
            {conta.sacosSugeridos > 0 && (
              <span className="secundario"> ({inteiro(conta.sacosSugeridos * pesoSaco)} kg)</span>
            )}
            <br />
            <span className="secundario">{conta.frase}</span>
            {/* A ÁGUA QUE PRODUZIU ESTA RECOMENDAÇÃO, dita junto com ela.
                Pedido do gestor em 21/09/2026: sem os números à vista, «nada a
                oferecer agora» é um veredito sem causa, e quem está no tanque
                não tem como conferir nem lembrar o que mediu de manhã. */}
            <br />
            <span className="secundario">{aguaDoDia(ponto, dia)}</span>
          </p>

          {ponto.tratos_hoje > 0 && (
            <div className="secundario">
              hoje: {numero(conta.sugerido, 0)} kg previstos · {ponto.tratos_hoje} registro
              {ponto.tratos_hoje > 1 ? 's' : ''} · {numero(ponto.tratado_hoje_kg, 0)} kg
              oferecidos
            </div>
          )}

          <div className="racao-atual">
            <span className="secundario">ração: {nomeDaRacao(racao)}</span>
            {racaoEscolhida && <span className="etiqueta etiqueta-fria">escolhida</span>}
            <button
              type="button"
              className="discreto"
              onClick={() => setTrocandoRacao((t) => !t)}
            >
              {trocandoRacao ? 'fechar' : 'trocar'}
            </button>
          </div>

          <AvisoDoGalpao
            racao={racao}
            galpao={galpao}
            dia={dia}
            pesoSaco={pesoSaco}
            aoUsar={(e) => {
              setRacaoEscolhida(e)
              lembrarRacao(ponto.tanque, e)
            }}
          />

          {trocandoRacao && (
            <EscolherRacao
              parametros={calculo.parametros}
              escolhida={racao}
              sugerida={conta.racao}
              compacto
              aoEscolher={(e) => {
                setRacaoEscolhida(e)
                lembrarRacao(ponto.tanque, e)
              }}
            />
          )}

          {/* ADIAR NÃO É PROIBIR. A água melhora sozinha ao longo da manhã, e
              o tratador já faz isso por conta: nas fichas de setembro, os dias
              de manhã ruim recebem a ração inteira à tarde. O app agora diz a
              mesma coisa em vez de calar — e não bloqueia nada, porque quem
              está lá pode ter motivo para tratar agora. */}
          {conta.acao === 'adiar' && (
            <p className="resposta adiar">
              <strong>Melhor deixar para a tarde.</strong> A água melhora com o sol,
              e o que for oferecido agora rende menos.
            </p>
          )}

          <Compositor
            tanque={ponto.tanque}
            fase={ponto.fase}
            sugerido={conta.sacosSugeridos}
            pesoSaco={pesoSaco}
            previstoKg={conta.sugerido}
            jaOferecidoKg={ponto.tratado_hoje_kg}
            salvando={salvando}
            aoConfirmar={oferecer}
          />
        </>
      )}

      {erro && <p className="aviso-erro">{erro}</p>}

      {marcandoFalta ? (
        <FormularioDeFalta
          titulo={`Falta no ${ponto.tanque}`}
          ciclos={[ponto.ciclo_id]}
          rotulos={{ [ponto.ciclo_id]: ponto.tanque }}
          motivos={motivos}
          dia={dia}
          aoFechar={() => setMarcandoFalta(false)}
          aoGravar={() => {
            setMarcandoFalta(false)
            aoGravar()
          }}
        />
      ) : (
        <button type="button" className="discreto" onClick={() => setMarcandoFalta(true)}>
          Não tratei este
        </button>
      )}
    </section>
  )
}


/**
 * A LINHA FECHADA DO TANQUE — e ela é o que decide se encolher ajuda.
 *
 * Formato do gestor: «T-5 75 | 50». Dado e previsto, nessa ordem, em quilo.
 * Ele chamou a própria sugestão de simplória; ela não é — é a menor coisa que
 * responde à pergunta da tela, que é «onde ainda falta tratar e quanto».
 *
 * Três estados e nada mais:
 *
 *   ✓  já recebeu o previsto ou mais
 *   ·  falta tratar, e o número diz quanto
 *   !  a água mandou não tratar, e aí número nenhum ajuda
 */
function ResumoDoTrato({
  ponto, calculo, curva, pesoSaco, dia,
}: {
  ponto: PontoDaRonda
  calculo: ParametrosDoCalculo
  curva: Curva
  pesoSaco: number
  dia: string
}) {
  const conta = calcular(ponto, calculo, curva, pesoSaco, dia)
  const dado = ponto.tratado_hoje_kg

  // A FALTA VEM ANTES DE TUDO. Sem isto, o tanque em que alguém marcou «não
  // tratei» ficava idêntico ao que ainda não foi tratado — «0 | 69» nos dois
  // —, e quem lesse a lista iria tratar um tanque que já tinha o dia fechado.
  if (ponto.faltas_hoje > 0) {
    return <span className="gaveta-falta">falta registrada</span>
  }

  if (conta.coeficiente === 0) {
    return <span className="gaveta-mal">! não tratar</span>
  }
  if (conta.sugerido === null) {
    return <span className="gaveta-resumo-num">{numero(dado, 0)} | —</span>
  }

  const cumpriu = dado >= conta.sugerido * 0.9
  return (
    <span className={cumpriu ? 'gaveta-bem' : 'gaveta-resumo-num'}>
      {cumpriu && '✓ '}
      {numero(dado, 0)} | {numero(conta.sugerido, 0)}
    </span>
  )
}

/**
 * O QUE JÁ ENTROU NESTE TANQUE NESTE DIA — um a um, com a hora.
 *
 * Aparece antes do compositor, porque é a resposta à pergunta que o tratador
 * faz ao chegar: «eu já tratei aqui?». Antes isso vivia numa linha cinza
 * miúda, e o gestor ficou clicando sem ver nada mudar — cada clique virando um
 * trato silencioso. O registro precisa aparecer do tamanho de um registro.
 *
 * ── POR QUE CONTAR NÃO BASTOU ─────────────────────────────────────────────
 *
 * Esta caixa dizia «3 tratos já enviados neste dia». Em 21/09/2026 o T-8
 * recebeu três tratos de 3 sacos — 10:28, 11:01 e 11:34 — e quem olhava a tela
 * sabia de um. O número estava certo e mesmo assim não serviu: ele responde
 * «quantos» quando a pergunta de quem chega no tanque é «QUAIS, e a que
 * horas». Sem a hora não dá para saber se o trato listado é o da manhã que eu
 * mesmo fiz ou o de alguém que passou antes.
 *
 * Agora a caixa lista cada oferta, em ordem de hora, com o ✓ do tamanho de um
 * registro. A manhã inteira aparece de uma vez, e o que falta se lê sozinho.
 *
 * O que está na FILA traz desfazer ao lado; o que já subiu, não. É a mesma
 * fronteira da tela da Fila: enquanto não sincronizou, apagar é apagar.
 */
function JaRegistrado({
  ponto, dia, pesoSaco, aoMudar,
}: {
  ponto: PontoDaRonda
  dia: string
  pesoSaco: number
  aoMudar: () => void
}) {
  const [anulando, setAnulando] = useState<string | null>(null)
  const naFila = tratosPendentesDe(ponto.ciclo_id, dia)
  const enviados = ponto.tratos_do_dia
  const anuladas = anulacoesPendentes()
  if (naFila.length === 0 && enviados.length === 0) return null

  const valendo = enviados.filter((x) => !anuladas.has(x.id)).length + naFila.length
  const totalKg =
    enviados.filter((x) => !anuladas.has(x.id)).reduce((t, x) => t + x.quantidade_kg, 0) +
    naFila.reduce((t, f) => {
      const q = Number(f.corpo['quantidade_informada'] ?? 0)
      return t + (f.corpo['unidade_informada'] === 'saco' ? q * pesoSaco : q)
    }, 0)

  return (
    <ul className="registrado">
      {enviados.map((t) => {
        // ANULAÇÃO NA FILA JÁ CONTA COMO FEITA, como todo o resto desta tela.
        // Sem isto, confirmar a anulação não mudava nada — o ✓ ficava, o total
        // ficava, o botão ficava — e ação sem sinal de ter acontecido é ação
        // que se repete. Foi assim que o T-8 recebeu três tratos.
        const anulado = anuladas.has(t.id)
        return (
          <Fragment key={t.id}>
            <li className={anulado ? 'registrado-item anulado' : 'registrado-item'}>
              <span className="registrado-marca">{anulado ? '✕' : '✓'}</span>
              <span>
                {/* A HORA VEM PRIMEIRO. É ela que permite reconhecer o trato:
                    «as 10:28 fui eu» é uma frase que alguém consegue dizer;
                    «o primeiro trato» não é. */}
                <strong>{horaCurta(t.hora)}</strong>{' '}
                {t.unidade_informada === 'saco'
                  ? `${emSacos(t.quantidade_informada)} ${t.quantidade_informada === 1 ? 'saco' : 'sacos'}`
                  : `${numero(t.quantidade_informada, 1)} kg`}
                <span className="secundario"> · {numero(t.quantidade_kg, 0)} kg</span>
                {anulado && <span className="secundario"> · anulado, na fila</span>}
              </span>
              {/* ANULAR MORA ONDE O TRATO APARECE. A tela da Fila cuida do que
                  ainda não subiu; aqui se cuida do que já subiu. Procurar o erro
                  numa terceira tela seria pedir que alguém saiba de cor qual
                  trato estava errado antes de ir vê-lo. */}
              {dentroDoPrazo(dia) && !anulado && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => setAnulando(anulando === t.id ? null : t.id)}
                >
                  {anulando === t.id ? 'deixa' : 'anular'}
                </button>
              )}
            </li>

            {/* A PERGUNTA FICA COLADA NA LINHA. Estava depois da lista inteira,
                e com três tratos no dia «anular este trato?» aparecia separada
                daquele a que se referia — o único sinal de qual estava
                escolhido era o botão ter virado «deixa», linhas acima e fora da
                tela de um celular. Confirmar a anulação errada não tem desfazer
                barato. */}
            {anulando === t.id && (
              <li className="registrado-item confirma-anular">
                <span>
                  Anular o trato das <strong>{horaCurta(t.hora)}</strong>, de{' '}
                  <strong>{numero(t.quantidade_kg, 0)} kg</strong>? Ele continua no
                  histórico, marcado como anulado, e sai das contas do dia.
                </span>
                <button
                  type="button"
                  className="confirma"
                  onClick={() => {
                    anularTrato(t.id, { rotulo: ponto.tanque, dia })
                    setAnulando(null)
                    aoMudar()
                  }}
                >
                  sim, anular
                </button>
              </li>
            )}
          </Fragment>
        )
      })}
      {naFila.map((f) => {
        const unidade = f.corpo['unidade_informada'] === 'kg' ? 'kg' : 'saco'
        const qtd = Number(f.corpo['quantidade_informada'] ?? 0)
        const kg = unidade === 'saco' ? qtd * pesoSaco : qtd
        return (
          <li key={f.id} className="registrado-item na-fila">
            <span className="registrado-marca">✓</span>
            <span>
              {unidade === 'saco'
                ? `${emSacos(qtd)} ${qtd === 1 ? 'saco' : 'sacos'} · ${numero(kg, 0)} kg`
                : `${numero(qtd, 1)} kg`}
              <span className="secundario"> · na fila</span>
            </span>
            <button
              type="button"
              className="discreto"
              onClick={() => {
                descartar(f.id)
                aoMudar()
              }}
            >
              desfazer
            </button>
          </li>
        )
      })}
          {/* O TOTAL DO DIA fecha a caixa. Sem ele a lista responde «quais» e
          deixa «quanto no total» para quem souber somar de cabeça no sol. */}
      <li className="registrado-item registrado-total">
        <span className="registrado-marca">Σ</span>
        <span>
          {/* A CONTAGEM DESCONTA O ANULADO, como o quilo. Dizer «150 kg · 3
              ofertas» seria as duas metades da mesma frase discordando. */}
          <strong>{numero(totalKg, 0)} kg</strong> neste dia ·{' '}
          {valendo} {valendo === 1 ? 'oferta' : 'ofertas'}
        </span>
      </li>
    </ul>
  )
}

/**
 * O GALPÃO NÃO TEM A RAÇÃO MARCADA — só aviso, NUNCA trava.
 *
 * Pedido do gestor em 25/09/2026, com a condição dita duas vezes: é aviso, e
 * nada de bloqueio. Quem está no tanque pode saber de uma pilha que o app não
 * sabe (a nota ainda não lançada, o saco emprestado do vizinho), e o trato
 * grava do mesmo jeito. O aviso só poupa o erro que antes se corrigia depois,
 * anulando trato por trato — e traz as que o galpão tem, a um toque.
 *
 * A conta, e quando ela cala, moram em `oGalpaoTem` (dominio/estoque.ts).
 */
function AvisoDoGalpao({
  racao, galpao, dia, pesoSaco, aoUsar,
}: {
  racao: Especificacao | null
  galpao: RacaoNoGalpao[] | null
  dia: string
  pesoSaco: number
  aoUsar: (e: Especificacao) => void
}) {
  if (!racao) return null
  // SÓ NO DIA DE HOJE. O galpão que o app conhece é o de hoje; o trato de
  // anteontem pode ter sido, de fato, da ração que acabou ontem — e o botão
  // «usar» gravaria no passado uma ração que ainda não tinha chegado.
  if (dia !== hojeISO()) return null
  const resposta = oGalpaoTem(paraFaixa(racao), galpao, movimentosDaFila(pendentes(), pesoSaco))
  if (resposta.tipo !== 'nao_tem') return null
  return (
    <div className="aviso-galpao">
      <span>
        O galpão não tem <strong>{nomeDaRacao(racao)}</strong>
        {resposta.outras.length > 0 ? '. Tem:' : ', nem outra com saldo. Se chegou ração, lance a compra em Estoque.'}
      </span>
      {resposta.outras.map((f) => {
        const e = deFaixa(f)
        return (
          <button key={nomeDaRacao(e)} type="button" className="discreto" onClick={() => aoUsar(e)}>
            usar {nomeDaRacao(e)}
          </button>
        )
      })}
    </div>
  )
}

function paraFaixa(e: Especificacao): FaixaDeRacao {
  return { min_mm: e.min, max_mm: e.max, proteina_pct: e.proteina }
}

function deFaixa(f: FaixaDeRacao): Especificacao {
  return { min: f.min_mm, max: f.max_mm, proteina: f.proteina_pct }
}

const RACAO_LEMBRADA = 'cunhatai.racao'

/**
 * A ração que ESTE tanque recebeu da última vez.
 *
 * Guardar por tanque, e não uma só para a ronda, é o que faz o alevinário
 * abrir com a ração do alevinário e a engorda com a da engorda. O caso que
 * pediu isto foi o A-1: a tabela sugeria 32% e o galpão tinha 36%, e o
 * tratador não pode ter de corrigir a mesma coisa onze vezes por semana.
 */
function racaoLembrada(tanque: string): Especificacao | null {
  try {
    const bruto = localStorage.getItem(RACAO_LEMBRADA)
    const mapa = bruto ? (JSON.parse(bruto) as Record<string, Especificacao>) : {}
    const e = mapa[tanque]
    return e && Number.isFinite(e.min) && Number.isFinite(e.proteina) ? e : null
  } catch {
    return null
  }
}

function lembrarRacao(tanque: string, e: Especificacao): void {
  try {
    const bruto = localStorage.getItem(RACAO_LEMBRADA)
    const mapa = bruto ? (JSON.parse(bruto) as Record<string, Especificacao>) : {}
    mapa[tanque] = e
    localStorage.setItem(RACAO_LEMBRADA, JSON.stringify(mapa))
  } catch {
    /* armazenamento bloqueado: a escolha vale só nesta sessão */
  }
}

const UNIDADE_LEMBRADA = 'cunhatai.unidade'

/**
 * A unidade que ESTE tanque usou da última vez.
 *
 * O alevinário trabalha em quilo e o resto em saco — e quem sabe disso é o
 * tanque, não a ronda. Herdar do último lançamento da volta faria o A-1 abrir
 * em sacos logo depois do T-8, que é justamente a exceção a proteger.
 */
function unidadeLembrada(tanque: string, fase: string): 'saco' | 'kg' {
  try {
    const bruto = localStorage.getItem(UNIDADE_LEMBRADA)
    const mapa = bruto ? (JSON.parse(bruto) as Record<string, string>) : {}
    const guardada = mapa[tanque]
    if (guardada === 'saco' || guardada === 'kg') return guardada
  } catch {
    /* armazenamento bloqueado: cai no padrão da fase */
  }
  return fase === 'alevinagem' ? 'kg' : 'saco'
}

function lembrarUnidade(tanque: string, unidade: 'saco' | 'kg'): void {
  try {
    const bruto = localStorage.getItem(UNIDADE_LEMBRADA)
    const mapa = bruto ? (JSON.parse(bruto) as Record<string, string>) : {}
    mapa[tanque] = unidade
    localStorage.setItem(UNIDADE_LEMBRADA, JSON.stringify(mapa))
  } catch {
    /* idem */
  }
}

/**
 * COMPOR E DEPOIS CONFIRMAR — o desenho é do gestor, e o motivo é bom.
 *
 * Os oito botões de saco que havia aqui antes não compunham nada: cada toque
 * era um ato completo e irreversível, sem estado intermediário. Não existia
 * «estou montando um lançamento», existia só «lancei». Numa tela sensível, com
 * a mão molhada, isso é armadilha — e foi.
 *
 * Agora há quatro blocos e uma ordem: a unidade, o número, o meio saco e o
 * confirma. Os três primeiros são estado, dá para ir e voltar à vontade; só o
 * quarto grava.
 *
 * A CAIXA JÁ VEM COM A SUGESTÃO DENTRO. Concordar é um toque no confirma;
 * discordar é ajustar e tocar. A regra que o gestor deu continua de pé:
 * discordar não pode ser mais difícil que concordar.
 *
 * O MEIO SACO TEM BOTÃO PRÓPRIO, e não é a seta andando de meio em meio:
 * chegar a 3 sacos viraria seis toques. Meio saco não é um número, é uma
 * categoria — e some quando a unidade é quilo, onde não quer dizer nada.
 */
function Compositor({
  tanque, fase, sugerido, pesoSaco, salvando, previstoKg, jaOferecidoKg, aoConfirmar,
}: {
  tanque: string
  fase: string
  sugerido: number
  pesoSaco: number
  salvando: boolean
  /** O previsto do DIA inteiro, para a confirmação poder comparar. */
  previstoKg: number | null
  /** O que este tanque já recebeu no dia, antes deste trato. */
  jaOferecidoKg: number
  aoConfirmar: (unidade: 'saco' | 'kg', quantidade: number) => void
}) {
  const [confirmando, setConfirmando] = useState(false)
  const [unidade, setUnidade] = useState<'saco' | 'kg'>(() => unidadeLembrada(tanque, fase))
  const [inteiros, setInteiros] = useState(() =>
    String(Math.max(0, unidade === 'saco' ? Math.floor(sugerido) : Math.round(sugerido * pesoSaco))),
  )
  const [meio, setMeio] = useState(() => unidade === 'saco' && sugerido % 1 >= 0.5)

  const passo = unidade === 'saco' ? 1 : 5
  const lido = numeroPtBr(inteiros) ?? NaN
  const n = Number.isFinite(lido) ? Math.max(0, lido) : 0
  const quantidade = n + (unidade === 'saco' && meio ? 0.5 : 0)
  const kg = unidade === 'saco' ? quantidade * pesoSaco : quantidade

  function trocarUnidade(nova: 'saco' | 'kg') {
    if (nova === unidade) return
    setUnidade(nova)
    lembrarUnidade(tanque, nova)
    // Converte o que já estava digitado, para o número não virar outra coisa
    // por baixo do dedo: 2 sacos viram 50 kg, e não 2 kg.
    if (nova === 'kg') {
      setInteiros(String(Math.round(quantidade * pesoSaco)))
      setMeio(false)
    } else {
      setInteiros(String(Math.floor(n / pesoSaco)))
    }
  }

  return (
    <div className="compositor">
      <div className="compositor-linha">
        <div className="unidade">
          <button
            type="button"
            className={unidade === 'saco' ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => trocarUnidade('saco')}
            aria-pressed={unidade === 'saco'}
          >
            Sc
          </button>
          <button
            type="button"
            className={unidade === 'kg' ? 'unidade-botao ativo' : 'unidade-botao'}
            onClick={() => trocarUnidade('kg')}
            aria-pressed={unidade === 'kg'}
          >
            kg
          </button>
        </div>

        <div className="contador">
          <button
            type="button"
            className="contador-seta"
            onClick={() => setInteiros(String(Math.max(0, n - passo)))}
            disabled={n <= 0}
            aria-label="diminuir"
          >
            −
          </button>
          <input
            value={inteiros}
            onChange={(e) => setInteiros(e.target.value.replace(/[^\d,.]/g, ''))}
            inputMode="numeric"
            aria-label={unidade === 'saco' ? 'quantos sacos' : 'quantos quilos'}
          />
          <button
            type="button"
            className="contador-seta"
            onClick={() => setInteiros(String(n + passo))}
            aria-label="aumentar"
          >
            +
          </button>
        </div>

        {unidade === 'saco' && (
          <button
            type="button"
            className={meio ? 'meio-botao ativo' : 'meio-botao'}
            onClick={() => setMeio((m) => !m)}
            aria-pressed={meio}
          >
            ½
          </button>
        )}

        <button
          type="button"
          className="confirma"
          disabled={salvando || quantidade <= 0}
          onClick={() => setConfirmando(true)}
        >
          ✓ registrar
        </button>
      </div>

      {/* DUPLA CONFIRMAÇÃO, SEMPRE — decisão do gestor em 21/09/2026.
          O desfazer existe enquanto não sincroniza, e mesmo assim: no dia em
          que o T-8 recebeu três tratos, ninguém usou o desfazer porque
          ninguém percebeu que havia o que desfazer. Confirmar é a única
          defesa que age ANTES do registro existir. */}
      {confirmando && (
        <div className="confirmacao">
          <p>
            <strong>Trato encerrado</strong>
            {frasePrevisto(kg, previstoKg, jaOferecidoKg)}
          </p>
          <div className="apuracao">
            <button type="button" className="discreto" onClick={() => setConfirmando(false)}>
              voltar
            </button>
            <button
              type="button"
              className="confirma"
              onClick={() => {
                aoConfirmar(unidade, quantidade)
                setMeio(false)
                setConfirmando(false)
              }}
            >
              sim, registrar
            </button>
          </div>
        </div>
      )}

      <div className="compositor-total secundario">
        {quantidade <= 0
          ? 'escolha a quantidade'
          : unidade === 'saco'
            ? `vai registrar ${emSacos(quantidade)} ${quantidade === 1 ? 'saco' : 'sacos'} · ${numero(kg, 0)} kg`
            : `vai registrar ${numero(kg, 1)} kg`}
      </div>
    </div>
  )
}

function FormularioDeFalta({
  titulo, ciclos, motivos, dia, rotulos, aoFechar, aoGravar,
}: {
  titulo: string
  ciclos: string[]
  rotulos: Record<string, string>
  motivos: MotivoDeFalta[]
  dia: string
  aoFechar: () => void
  aoGravar: () => void
}) {
  const [motivo, setMotivo] = useState(motivos[0]?.codigo ?? '')
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)

  const escolhido = motivos.find((m) => m.codigo === motivo)
  const tanques = ciclos.map((c) => rotulos[c] ?? '—')
  const hoje = hojeISO()

  return (
    <form
      className="nova-pessoa"
      onSubmit={(evento) => {
        evento.preventDefault()
        // DUPLA CONFIRMAÇÃO, como no trato — decisão do gestor de 21/09/2026,
        // estendida à falta em 24/09/2026. A falta trava mais que o trato:
        // ela FECHA O DIA do tanque, e no «Ninguém tratou» fecha o de todos
        // de uma vez. Antes, dois toques distraídos gravavam, com o primeiro
        // motivo da lista marcado sozinho.
        if (!confirmando) {
          setConfirmando(true)
          return
        }
        setSalvando(true)
        setErro(null)
        try {
          registrarFalta(
            ciclos, dia, motivo,
            observacao.trim() === '' ? null : observacao.trim(),
            rotulos,
          )
          aoGravar()
        } catch (falha) {
          setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
        } finally {
          setSalvando(false)
        }
      }}
    >
      <span className="campo-rotulo">{titulo}</span>
      <label className="campo">
        <span className="campo-rotulo">Por quê?</span>
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          {motivos.map((m) => (
            <option key={m.codigo} value={m.codigo}>{m.descricao}</option>
          ))}
        </select>
        {/* A classificação sai do motivo e NUNCA é digitada. Mostrar aqui deixa
            visível que a escolha tem consequência no relatório. */}
        <span className="secundario">
          entra como{' '}
          {motivos.find((m) => m.codigo === motivo)?.classificacao ?? '—'}
        </span>
      </label>
      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </label>
      {erro && <p className="aviso-erro">{erro}</p>}

      {confirmando ? (
        <div className="confirmacao">
          {/* A pergunta diz o que vai ficar gravado: quantos tanques, quais,
              que dia e que motivo — o motivo escolhido sozinho é justamente o
              que passava sem ninguém ler. */}
          <p>
            Marcar falta em{' '}
            <strong>
              {tanques.length === 1 ? tanques[0] : `${tanques.length} tanques`}
            </strong>
            {tanques.length > 1 && <> ({tanques.join(', ')})</>} em{' '}
            <strong>{diaCurto(dia)}</strong>
            {dia !== hoje && <strong> — que não é hoje</strong>}, motivo{' '}
            <strong>{escolhido?.descricao ?? '—'}</strong>?
          </p>
          <p className="secundario">
            {tanques.length === 1 ? 'Esse tanque não aceita' : 'Esses tanques não aceitam'} mais
            trato neste dia. Enquanto não sincronizar, dá para desfazer na lista do
            Sincronizar; depois, a falta não se desfaz pelo app.
          </p>
          <div className="apuracao">
            <button type="button" className="discreto" onClick={() => setConfirmando(false)}>
              voltar
            </button>
            <button type="submit" className="confirma" disabled={salvando}>
              {salvando ? 'Gravando…' : 'sim, marcar falta'}
            </button>
          </div>
        </div>
      ) : (
        <div className="acoes">
          <button type="submit">Registrar falta</button>
          <button type="button" className="discreto" onClick={aoFechar}>
            Cancelar
          </button>
        </div>
      )}
    </form>
  )
}

// ---------------------------------------------------------------------------
// A conta
// ---------------------------------------------------------------------------

interface Conta {
  pesoHoje: number | null
  biomassa: number | null
  tendencia: ReturnType<typeof tendenciaDoOxigenio>
  sugerido: number | null
  sacosSugeridos: number
  coeficiente: number
  acao: 'reduzir' | 'adiar' | null
  limitou: string | null
  frase: string
  porque: string
  racao: { min: number; max: number; proteina: number } | null
}

function calcular(
  ponto: PontoDaRonda,
  calculo: ParametrosDoCalculo,
  curva: Curva,
  pesoSaco: number,
  dia: string,
): Conta {
  const vazia: Conta = {
    pesoHoje: null, biomassa: null, tendencia: 'sem_serie', sugerido: null,
    sacosSugeridos: 0, coeficiente: 1, acao: null, limitou: null,
    frase: '', porque: '', racao: null,
  }

  const ancora = ponto.peso_medido_g ?? ponto.peso_medio_g
  const dataAncora = ponto.data_medida ?? ponto.data_povoamento
  if (!ancora || !dataAncora) return { ...vazia, porque: 'este ciclo não tem peso de entrada.' }

  // OS DIAS CONTAM ATÉ O DIA ESCOLHIDO, não até agora. Com o seletor de data,
  // usar o relógio aqui faria o peixe de 20 dias atrás ser estimado com o peso
  // de hoje — e a sugestão sairia grande demais para o dia que se preenche.
  const dias = Math.max(
    0,
    Math.round((meiaNoite(dia) - new Date(dataAncora).getTime()) / 86_400_000),
  )
  const pesoHoje = pesoApos(g(ancora), dias, curva)
  const biomassa = (ponto.peixes_hoje * pesoHoje) / 1000
  const tendencia = tendenciaDoOxigenio(ponto.leituras)

  const faixa = calculo.arracoamento.find(
    (f) => pesoHoje >= f.peso_de_g && pesoHoje < f.peso_ate_g,
  )
  if (!faixa) {
    return { ...vazia, pesoHoje, biomassa, tendencia,
      porque: `nenhuma faixa da tabela cobre ${inteiro(pesoHoje)} g.` }
  }

  const area = m2ParaHa(m2(ponto.area_m2))
  // O teto muda com a ESTAÇÃO, e a estação é a do dia lançado. Lançar um dia
  // de abril em maio pegaria o teto da seca para um dia de águas.
  const teto = tetoDoTanque(calculo.tetos, ponto.tanque_id, mesDoDia(dia))
  if (teto === null) {
    return { ...vazia, pesoHoje, biomassa, tendencia,
      porque: 'este tanque não tem teto de água cadastrado para este mês.' }
  }

  const limitePeixe = biomassa * (pctDoPesoVivo(faixa, tendencia) / 100)
  const limiteAgua = teto * area
  const aguaManda = limiteAgua < limitePeixe
  const base = Math.min(limitePeixe, limiteAgua)

  // SEM LEITURA DO DIA, VALE A ÚLTIMA CONHECIDA — decisão do gestor.
  //
  // O comportamento anterior era pior e parecia neutro: sem leitura, nenhum
  // corte. Só que «não medi» não é notícia boa. Num tanque que passou dois
  // meses entre 1 e 3 mg/L, a manhã em que ninguém mediu não vira uma manhã de
  // água boa — vira uma manhã sobre a qual não se sabe nada, e a última coisa
  // que se soube era ruim. Oferecer o teto cheio justamente aí seria usar a
  // falta de informação como permissão.
  //
  // A leitura só volta a ser nula quando o tanque nunca foi lido.
  //
  // E O ECO É DE CADA MEDIDA (04/10/2026): temperatura de hoje não apaga o
  // oxigênio ruim de ontem. Ver `aguaDaConta`, que tem teste.
  const agua = aguaDaConta(ponto.leituras, dia)
  const veredito = vereditoDaAgua(
    agua.oxigenio?.valor ?? null,
    agua.temperatura?.valor ?? null,
    calculo.coeficientes,
  )

  const sugerido = base * veredito.coeficiente
  // Desconta o que já saiu hoje, e arredonda PARA BAIXO: o erro barato é
  // sobrar ração no galpão, o caro é sobrar ração na água.
  const restante = Math.max(0, sugerido - ponto.tratado_hoje_kg)
  const sacosSugeridos = Math.floor((restante / pesoSaco) * 2) / 2

  // DE ONDE VEIO O NÚMERO QUE MANDOU — e se ele é de hoje ou é eco.
  const fonte: MedidaUsada | null =
    veredito.limitou === 'oxigenio' ? agua.oxigenio
      : veredito.limitou === 'temperatura' ? agua.temperatura
        : null
  const eco = fonte !== null && !fonte.doDia
  const nadaHoje = !agua.oxigenio?.doDia && !agua.temperatura?.doDia
  const ultimaData = [agua.oxigenio?.data, agua.temperatura?.data]
    .filter((d): d is string => d !== undefined)
    .sort()
    .pop()
  const deQuando = fonte ? diaCurto(fonte.data) : ultimaData ? diaCurto(ultimaData) : ''

  let frase: string
  if (veredito.limitou === 'sem_leitura') {
    // «Nunca foi lido» só quando não há leitura nenhuma. Com só temperatura
    // no histórico (de outro dia), o que falta é oxigênio — e a temperatura
    // não passa de um dia para o outro.
    frase = ponto.leituras.length === 0
      ? `este tanque nunca foi lido — usando o limite ${aguaManda ? 'da água' : 'do peixe'} sem corte.`
      : `sem leitura hoje, e nenhum oxigênio medido antes — usando o limite ${aguaManda ? 'da água' : 'do peixe'} sem corte.`
  } else if (veredito.limitou === 'oxigenio' || veredito.limitou === 'temperatura') {
    const quem = veredito.limitou === 'oxigenio' ? 'o oxigênio' : 'a temperatura'
    const v = veredito.valor === null ? '' :
      veredito.limitou === 'oxigenio'
        ? `${numero(veredito.valor, 1)} mg/L`
        : `${numero(veredito.valor, 1)} °C`
    // «sem leitura hoje» só quando não houve leitura nenhuma; com a outra
    // medida do dia, diz qual faltou — senão a frase desmente a de baixo.
    const semQue = nadaHoje
      ? 'sem leitura hoje'
      : veredito.limitou === 'oxigenio' ? 'sem oxigênio hoje' : 'sem temperatura hoje'
    frase = eco
      ? `${semQue} — mantendo os ${Math.round(veredito.coeficiente * 100)}% de ${deQuando}, quando ${quem} estava em ${v}.`
      : `${Math.round(veredito.coeficiente * 100)}% do previsto — quem limitou foi ${quem}, ${v}.`
  } else if (nadaHoje) {
    frase = aguaManda
      ? `sem leitura hoje — o último oxigênio, de ${deQuando}, não pedia corte. Quem limita é a água: ${inteiro(teto)} kg/ha/dia × ${numero(area, 2)} ha.`
      : `sem leitura hoje — o último oxigênio, de ${deQuando}, não pedia corte. Quem limita é o peixe: ${numero(pctDoPesoVivo(faixa, tendencia), 1)}% do peso vivo.`
  } else {
    frase = aguaManda
      ? `quem limitou foi a água: ${inteiro(teto)} kg/ha/dia × ${numero(area, 2)} ha.`
      : `quem limitou foi o peixe: ${numero(pctDoPesoVivo(faixa, tendencia), 1)}% do peso vivo.`
  }

  return {
    pesoHoje, biomassa, tendencia, sugerido, sacosSugeridos,
    coeficiente: veredito.coeficiente,
    acao: veredito.acao,
    limitou: veredito.limitou ?? (aguaManda ? 'agua' : 'peixe'),
    frase, porque: '',
    racao: lerRacao(faixa.racao_indicada),
  }
}

/**
 * Lê a especificação da ração do texto da tabela — «6 a 8 mm, 28%».
 *
 * Quando a proteína vem em faixa («28 a 32%»), fica a MAIOR.
 *
 * Eu havia feito o contrário, argumentando nitrogênio na água. Estava errado,
 * e o gestor pegou pelo caso concreto: o A-1 apareceu pedindo «3 a 4 mm, 28%»,
 * e essa ração não existe nesta fazenda. Aqui a granulometria anda casada com
 * a proteína — 8a10 e 6a8 são 28%, 4a6 e 3a4 são 32%, 2a3 é 36% —, então a
 * ponta de baixo da faixa descreve um saco que ninguém compra.
 *
 * A lição é mais geral que a correção: a tabela é referência de literatura, e
 * quando ela discorda do galpão, quem manda é o galpão.
 */
function lerRacao(texto: string | null): { min: number; max: number; proteina: number } | null {
  if (!texto) return null
  const num = (s: string) => Number(s.replace(',', '.'))

  const mm = texto.match(/([\d,.]+)\s*a\s*([\d,.]+)\s*mm/i)
  const pct = texto.match(/([\d,.]+)(?:\s*a\s*([\d,.]+))?\s*%/)
  if (!mm || !pct) return null

  const min = num(mm[1] ?? '')
  const max = num(mm[2] ?? '')
  const proteina = num(pct[2] ?? pct[1] ?? '')
  if (![min, max, proteina].every((n) => Number.isFinite(n) && n > 0)) return null
  return { min, max, proteina }
}

/** Meia-noite do dia escolhido, no fuso do aparelho. */
function meiaNoite(iso: string): number {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(a ?? 2026, (m ?? 1) - 1, d ?? 1).getTime()
}

function mesDoDia(iso: string): number {
  return Number(iso.slice(5, 7))
}

/**
 * A água que produziu a recomendação, em uma linha.
 *
 * Usa a leitura do PRÓPRIO DIA quando existe; quando não existe, diz de quando
 * é o eco, porque é exatamente isso que o cálculo está fazendo — o coeficiente
 * não volta a 1 só porque ninguém mediu.
 */
function aguaDoDia(ponto: PontoDaRonda, dia: string): string {
  // A MESMA CONTA DO TRATO, medida por medida — a frase não pode dizer uma
  // água e a conta usar outra.
  const agua = aguaDaConta(ponto.leituras, dia)
  const { oxigenio, temperatura } = agua
  if (!oxigenio && !temperatura) {
    return ponto.leituras.length === 0
      ? 'Sem leitura de água neste tanque.'
      : 'Sem leitura hoje — e a temperatura não vale de um dia para o outro.'
  }

  const algumaHoje = oxigenio?.doDia === true || temperatura?.doDia === true
  // As duas do mesmo dia passado: a frase de sempre, com a data uma vez só.
  // Mistura (temperatura de hoje, oxigênio de ontem): cada número diz de
  // quando é, senão o 2,4 de ontem passaria por medição de hoje.
  const mesmoDiaPassado =
    !algumaHoje && (!oxigenio || !temperatura || oxigenio.data === temperatura.data)
  const parte = (m: MedidaUsada | null, texto: string): string | null =>
    m === null ? null : m.doDia || mesmoDiaPassado ? texto : `${texto} (de ${diaCurto(m.data)})`
  const partes = [
    parte(temperatura, temperatura ? `${numero(temperatura.valor, 1)} °C` : ''),
    parte(oxigenio, oxigenio ? `${numero(oxigenio.valor, 1)} mg/L` : ''),
  ].filter((x) => x !== null)
  const medidas = partes.join(' · ')

  // A HORA IMPORTA agora que há mais de uma leitura por dia: sem ela, duas
  // recomendações diferentes no mesmo dia parecem contradição em vez de duas
  // medições. Quando as duas medidas de hoje são de leituras diferentes, cada
  // número leva a própria hora — senão os 26,5 °C das seis da manhã passariam
  // por água fria às três da tarde.
  const hm = (h: string | null) => (h ? h.slice(0, 5) : null)
  const deHoje = [oxigenio, temperatura].filter((m): m is MedidaUsada => m?.doDia === true)
  const umaHora = new Set(deHoje.map((m) => hm(m.hora))).size <= 1
  if (algumaHoje && !umaHora) {
    const comHora = (m: MedidaUsada | null, texto: string): string | null =>
      m === null ? null
        : m.doDia ? `${texto}${hm(m.hora) ? ` (${hm(m.hora)})` : ''}`
          : `${texto} (de ${diaCurto(m.data)})`
    const porMedida = [
      comHora(temperatura, temperatura ? `${numero(temperatura.valor, 1)} °C` : ''),
      comHora(oxigenio, oxigenio ? `${numero(oxigenio.valor, 1)} mg/L` : ''),
    ].filter((x) => x !== null)
    return `Água de hoje: ${porMedida.join(' · ')}.`
  }
  const horaDoDia = deHoje.length > 0 ? hm(deHoje[0]?.hora ?? null) : null
  const hora = horaDoDia ? ` das ${horaDoDia}` : ''
  if (algumaHoje) return `Água de hoje${hora}: ${medidas}.`
  const quando = diaCurto((oxigenio ?? temperatura)?.data ?? dia)
  return mesmoDiaPassado
    ? `Sem leitura hoje — valendo a de ${quando}: ${medidas}.`
    : `Sem leitura hoje — valendo as últimas: ${medidas}.`
}

/**
 * O QUE ESTE TRATO FAZ COM O DIA — em uma frase, antes de gravar.
 *
 * Texto do gestor: «Trato encerrado», e quando necessário «trato encerrado X
 * vezes mais, ou X% mais ou menos do previsto».
 *
 * Só aparece quando há o que dizer. Trato que fecha o dia dentro do previsto
 * não ganha adjetivo nenhum — acrescentar «100% do previsto» a todo registro
 * treinaria a pessoa a não ler a linha, e aí ela não leria no dia em que a
 * linha dissesse 4×.
 *
 * Compara o DIA INTEIRO, e não este trato sozinho: foi somando três tratos
 * certos que o T-8 chegou a quatro vezes o previsto.
 */
function frasePrevisto(
  esteKg: number,
  previstoKg: number | null,
  jaOferecidoKg: number,
): string {
  if (previstoKg === null || previstoKg <= 0) return '.'
  const total = jaOferecidoKg + esteKg
  const razao = total / previstoKg

  // Uma décima de folga para os dois lados: quem ofertou 73 kg contra 72
  // previstos acertou, e dizer «1% a mais» seria ruído com cara de aviso.
  if (razao > 0.9 && razao < 1.1) return `, ${numero(total, 0)} kg no dia — o previsto.`

  if (razao >= 2) {
    return (
      `, ${numero(total, 0)} kg no dia — ` +
      `${numero(razao, razao % 1 === 0 ? 0 : 1)} vezes mais que o previsto ` +
      `de ${numero(previstoKg, 0)} kg.`
    )
  }
  const pct = Math.abs(Math.round((razao - 1) * 100))
  return (
    `, ${numero(total, 0)} kg no dia — ${pct}% ${razao > 1 ? 'a mais' : 'a menos'} ` +
    `que o previsto de ${numero(previstoKg, 0)} kg.`
  )
}

/**
 * TRÊS DIAS, e o limite é do gestor.
 *
 * «Colocaria um limitador do alcance de atuação deste botão, deixaria ele
 * disponível para anular tratos por no máximo 3 dias atrás.»
 *
 * Passada a semana o número já foi lido: entrou na conta do mês, virou
 * conversa, talvez virou decisão. Corrigir ali não é mais corrigir — é
 * reescrever o que os outros já usaram. O servidor confere de novo, porque a
 * tela de um celular pode estar velha e a fila pode segurar um pedido por dias.
 */
function dentroDoPrazo(dia: string): boolean {
  const d = Math.round((meiaNoite(hojeISO()) - meiaNoite(dia)) / 86_400_000)
  return d >= 0 && d <= 3
}

/** «10:28» — hora e minuto, que é o que a ficha de papel sempre trouxe. */
function horaCurta(hora: string | null): string {
  return hora ? hora.slice(0, 5) : '—'
}

/** «18/09» — o suficiente para o tratador saber de quando é o eco. */
function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}`
}

function agora(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
